import React from "react";
import { Box, Grid } from "@mui/material";
import type {
  VenueData,
  IContactData,
  VenueContact,
  SimilarVenue,
} from "../../types/venues.types";
import BasicInfoCard from "../cards/BasicInfoCard";
import PowerInfoCard from "../cards/PowerInfoCard";
import PASystemInfoCard from "../cards/PASystemInfoCard";
import ContactsCard from "./ContactCard";
import BookingsCard from "../cards/BookingsCard";
import VenueInternetSection from "../cards/VenueInternetSection";
import SimilarVenuesCard from "../cards/SimilarVenuesCard";

interface BookingResponse {
  id: number;
  title?: string;
  service?: string;
  package_item?: { name: string };
  services?: Array<{ date_of_service: string }>;
  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string;
  }>;
}

interface VenueDetailsTabProps {
  venueData: VenueData;
  // Bookings
  bookings: BookingResponse[];
  totalBookings: number;
  bookingsLoading: boolean;
  bookingsPage: number;
  bookingsRowsPerPage: number;
  onBookingsPageChange: (event: unknown, newPage: number) => void;
  onBookingsRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onBookingClick: (bookingId: number) => void;
  // Contacts
  paginatedContacts: VenueContact[];
  contactPage: number;
  contactRowsPerPage: number;
  onContactPageChange: (event: unknown, newPage: number) => void;
  onContactRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAddContactClick: () => void;
  onContactEdit: (updatedContact: IContactData) => void;
  // Similar Venues
  similarVenues: SimilarVenue[];
  similarVenuesLoading: boolean;
  onSimilarVenueClick: (venueId: number) => void;
  onMergeClick: (secondaryVenueId: number) => void;
  canMerge: boolean;
  // Theme
  formatDate: (date: string | undefined) => string;
  // Components passed as props
  InfoCard: React.ComponentType<{
    title: string;
    icon: React.ReactNode;
    sx?: object;
    actionIcon?: React.ReactNode;
    onActionClick?: () => void;
    children: React.ReactNode;
  }>;
  InfoRow: React.ComponentType<{
    label: string;
    value: string;
  }>;
  ContactDetailsDialog: React.ComponentType<{
    contact: IContactData;
    onEdit: (updatedContact: IContactData) => void;
    inline?: boolean;
    showActionsInline?: boolean;
  }>;
}

export const VenueDetailsTab: React.FC<VenueDetailsTabProps> = ({
  venueData,
  bookings,
  totalBookings,
  bookingsLoading,
  bookingsPage,
  bookingsRowsPerPage,
  onBookingsPageChange,
  onBookingsRowsPerPageChange,
  onBookingClick,
  paginatedContacts,
  contactPage,
  contactRowsPerPage,
  onContactPageChange,
  onContactRowsPerPageChange,
  onAddContactClick,
  onContactEdit,
  similarVenues,
  similarVenuesLoading,
  onSimilarVenueClick,
  onMergeClick,
  canMerge,
  formatDate,
  InfoCard,
  InfoRow,
  ContactDetailsDialog,
}) => {
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <BasicInfoCard
            venueData={venueData}
            InfoCard={InfoCard}
            InfoRow={InfoRow}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PowerInfoCard
            venueData={venueData}
            InfoCard={InfoCard}
            InfoRow={InfoRow}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <VenueInternetSection
            venueData={venueData}
            InfoCard={InfoCard}
            InfoRow={InfoRow}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <PASystemInfoCard
            venueData={venueData}
            InfoCard={InfoCard}
            InfoRow={InfoRow}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <ContactsCard
            contacts={venueData.contacts}
            paginatedContacts={paginatedContacts}
            contactPage={contactPage}
            contactRowsPerPage={contactRowsPerPage}
            onPageChange={onContactPageChange}
            onRowsPerPageChange={onContactRowsPerPageChange}
            onAddClick={onAddContactClick}
            onContactEdit={onContactEdit}
            InfoCard={InfoCard}
            ContactDetailsDialog={ContactDetailsDialog}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <BookingsCard
            bookings={bookings}
            totalBookings={totalBookings}
            bookingsLoading={bookingsLoading}
            page={bookingsPage}
            rowsPerPage={bookingsRowsPerPage}
            onPageChange={onBookingsPageChange}
            onRowsPerPageChange={onBookingsRowsPerPageChange}
            onBookingClick={onBookingClick}
            formatDate={formatDate}
            InfoCard={InfoCard}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SimilarVenuesCard
            similarVenues={similarVenues}
            loading={similarVenuesLoading}
            onVenueClick={onSimilarVenueClick}
            onMergeClick={onMergeClick}
            canMerge={canMerge}
            InfoCard={InfoCard}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenueDetailsTab;
